// /electionday — always-visible national + per-district readout.
//
// Composition:
//   • Call-state banner (INSUFFICIENT / DIRECTIONAL / PROJECTED CALL)
//   • National headline tiles — PL / PN / Abstained / Districts.
//     The PL + PN tiles drop to bare counts in INSUFFICIENT and pick
//     up share + 95% interval in DIRECTIONAL / PROJECTED. Abstain
//     rate is rendered at the same weight as the tally throughout,
//     because it is the headline diagnostic of sample quality.
//   • "N more clean reads to projected call" — derived from the
//     measured accept rate, never an assumed one.
//   • Per-district table — district | PL | PN | abstain | turnout
//     ballots | eligible voters.
//   • Methodology + known-failure-modes — one click away (collapsed
//     by default, see MethodologyNote).
//
// State source: the parent (ElectionDayPage) supplies an array of
// committed batches plus the current uncommitted batch. We pool them
// and run computeStats — the stats module is the single source of
// truth for every derived number rendered here.

import { useMemo } from 'react';
import {
  computeStats,
  MIN_CLEAN_DIRECTIONAL,
  MIN_DISTRICTS_COVERED,
  TOTAL_DISTRICTS,
  type Batch,
  type CallState,
  type NationalStats,
} from './stats';
import MethodologyNote from './MethodologyNote';

export interface StatsPanelProps {
  committed: Batch[];
  current: Batch | null;
}

export default function StatsPanel({ committed, current }: StatsPanelProps) {
  const stats = useMemo<NationalStats>(
    () => computeStats(current ? [...committed, current] : committed),
    [committed, current],
  );

  const callIsLive = stats.callState !== 'INSUFFICIENT';

  return (
    <section className="electionday-stats" aria-label="National read">
      <header className="electionday-stats__head">
        <h2 className="electionday-stats__title">National read</h2>
        <CallBadge state={stats.callState} leader={stats.leader} />
      </header>

      <div className="electionday-stats__tiles">
        <Tile
          label="PL"
          variant="pl"
          big={stats.pl.toLocaleString()}
          sub={
            callIsLive
              ? `${(stats.plShare * 100).toFixed(1)}%`
              : 'clean reads'
          }
          ci={
            callIsLive
              ? `${(stats.plInterval.lo * 100).toFixed(1)}% – ${(stats.plInterval.hi * 100).toFixed(1)}%`
              : null
          }
        />
        <Tile
          label="PN"
          variant="pn"
          big={stats.pn.toLocaleString()}
          sub={
            callIsLive
              ? `${((1 - stats.plShare) * 100).toFixed(1)}%`
              : 'clean reads'
          }
          ci={
            callIsLive
              ? `${(stats.pnInterval.lo * 100).toFixed(1)}% – ${(stats.pnInterval.hi * 100).toFixed(1)}%`
              : null
          }
        />
        <Tile
          label="Abstained"
          variant="abstain"
          big={stats.abstained.toLocaleString()}
          sub={
            stats.totalStills > 0
              ? `${(stats.abstainRate * 100).toFixed(1)}% rate`
              : '0% rate'
          }
          ci={null}
        />
        <Tile
          label="Districts"
          variant="districts"
          big={`${stats.districtsCovered} / ${TOTAL_DISTRICTS}`}
          sub={
            stats.districtsCovered < MIN_DISTRICTS_COVERED
              ? `need ≥ ${MIN_DISTRICTS_COVERED} for call`
              : 'sample spread OK'
          }
          ci={null}
        />
      </div>

      <ProgressLine stats={stats} />

      {stats.tbcStills > 0 && (
        <p className="electionday-stats__tbc">
          {stats.tbcStills.toLocaleString()} still
          {stats.tbcStills === 1 ? '' : 's'} sitting under a “TBC” batch —
          set the district below to roll those into the national stats.
        </p>
      )}

      <DistrictTable stats={stats} />

      <MethodologyNote />
    </section>
  );
}

// ── Sub-components ─────────────────────────────────────────────────

interface TileProps {
  label: string;
  variant: 'pl' | 'pn' | 'abstain' | 'districts';
  big: string;
  sub: string;
  ci: string | null;
}

function Tile({ label, variant, big, sub, ci }: TileProps) {
  return (
    <div className={`electionday-stats__tile electionday-stats__tile--${variant}`}>
      <span className="electionday-stats__tile-label">{label}</span>
      <span className="electionday-stats__tile-big">{big}</span>
      <span className="electionday-stats__tile-sub">{sub}</span>
      {ci && (
        <span className="electionday-stats__tile-ci">95% CI {ci}</span>
      )}
    </div>
  );
}

interface CallBadgeProps {
  state: CallState;
  leader: 'PL' | 'PN' | null;
}

function CallBadge({ state, leader }: CallBadgeProps) {
  const label =
    state === 'PROJECTED'
      ? leader
        ? `Projected call — ${leader}`
        : 'Projected call'
      : state === 'DIRECTIONAL'
        ? leader
          ? `Directional — indicative · ${leader} ahead`
          : 'Directional — indicative'
        : 'Insufficient — no call';
  return (
    <span
      className={`electionday-stats__badge electionday-stats__badge--${state.toLowerCase()}`}
    >
      {label}
    </span>
  );
}

interface ProgressLineProps {
  stats: NationalStats;
}

function ProgressLine({ stats }: ProgressLineProps) {
  if (stats.callState === 'PROJECTED') {
    return (
      <p className="electionday-stats__progress">
        Projected-call threshold met: {stats.clean.toLocaleString()} clean
        reads pooled across {stats.districtsCovered} districts.
      </p>
    );
  }
  if (stats.clean === 0) {
    return (
      <p className="electionday-stats__progress">
        No clean reads yet. Commit a batch with a district set to start
        the national tally.
      </p>
    );
  }
  const stillsHint =
    stats.stillsToProjected !== null
      ? ` · ≈ ${stats.stillsToProjected.toLocaleString()} more stills at the current ${Math.round(
          stats.acceptRate * 100,
        )}% accept rate`
      : '';
  const directionalHint =
    stats.callState === 'INSUFFICIENT' &&
    stats.clean < MIN_CLEAN_DIRECTIONAL
      ? ` · ${(MIN_CLEAN_DIRECTIONAL - stats.clean).toLocaleString()} more for directional`
      : '';
  return (
    <p className="electionday-stats__progress">
      {stats.cleanReadsToProjected.toLocaleString()} more clean reads to
      projected call{stillsHint}
      {directionalHint}.
    </p>
  );
}

interface DistrictTableProps {
  stats: NationalStats;
}

function DistrictTable({ stats }: DistrictTableProps) {
  if (stats.byDistrict.length === 0) {
    return (
      <p className="electionday-stats__districts-empty">
        No districts yet. Tag a batch with a district in the ballot panel
        below and commit it to populate this table.
      </p>
    );
  }
  return (
    <div className="electionday-stats__district-wrap">
      <table className="electionday-stats__district-table">
        <thead>
          <tr>
            <th>District</th>
            <th>PL</th>
            <th>PN</th>
            <th>Abstained</th>
            <th>Clean</th>
            <th>Abstain rate</th>
            <th>Turnout</th>
            <th>Eligible</th>
          </tr>
        </thead>
        <tbody>
          {stats.byDistrict.map((d) => {
            const abstainRate =
              d.totalStills > 0 ? d.abstained / d.totalStills : 0;
            return (
              <tr key={d.district}>
                <th scope="row">{d.district}</th>
                <td>{d.pl}</td>
                <td>{d.pn}</td>
                <td>{d.abstained}</td>
                <td>{d.clean}</td>
                <td>{(abstainRate * 100).toFixed(0)}%</td>
                <td>
                  {d.turnoutBallots !== null
                    ? d.turnoutBallots.toLocaleString()
                    : '—'}
                </td>
                <td>{d.eligibleVoters.toLocaleString()}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="electionday-stats__district-note">
        Turnout figures populate once the electoral commission counts
        land. Eligible-voter counts are placeholders (
        {(stats.byDistrict[0]?.eligibleVoters ?? 0).toLocaleString()} per
        district — national / 13) until real per-district numbers are
        wired in. Estimator is ready to switch to a weighted stratified
        proportion with no UI changes.
      </p>
    </div>
  );
}
